import { styled, alpha } from "@mui/material/styles";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import SettingsIcon from "@mui/icons-material/Settings";
import CategoryIcon from "@mui/icons-material/Category";
import RuleIcon from "@mui/icons-material/Rule";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { CircularProgress, IconButton, Stack } from "@mui/material";
import { useDialog } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import InfoWarningsDialogContent from "./InfoWarnings/InfoWarningsDialogContent";
import { FunctionComponent, useState } from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { DeviceStatusRequestMessage } from "Infrastructure/Models/Messages/Messages";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import BoltIcon from "@mui/icons-material/Bolt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import { BlTooltip } from "Shared/BlTooltip";
import FrequentActionsDialogContent from "./FrequentActions/FrequentActionsDialogContent";
import SystemSettingsDialogContent from "./SystemSettings/SystemSettingsDialogContent";
import FeaturesDialogContent from "./Features/FeaturesDialogContent";
import ProductsDialogContent from "./Products/ProductsDialogContent";
import { useExportDevicesMutation } from "InstanceManagement/Api/Mutations/useExportDevicesMutation";
import { useUserInfo } from "Shared/Contexts/UserInfoContext";

const StyledMenu = styled((props: MenuProps) => (
	<Menu
		elevation={0}
		anchorOrigin={{
			vertical: "bottom",
			horizontal: "right",
		}}
		transformOrigin={{
			vertical: "top",
			horizontal: "right",
		}}
		{...props}
	/>
))(({ theme }) => ({
	"& .MuiPaper-root": {
		borderRadius: 6,
		marginTop: theme.spacing(1),
		minWidth: 180,
		color:
			theme.palette.mode === "light"
				? "rgb(55, 65, 81)"
				: theme.palette.grey[300],
		boxShadow:
			"rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
		"& .MuiMenu-list": {
			padding: "4px 0",
		},
		"& .MuiMenuItem-root": {
			"& .MuiSvgIcon-root": {
				fontSize: 18,
				color: theme.palette.text.secondary,
				marginRight: theme.spacing(1.5),
			},
			"&:active": {
				backgroundColor: alpha(
					theme.palette.primary.main,
					theme.palette.action.selectedOpacity
				),
			},
		},
	},
}));

interface Props extends MenuProps {
	isDisabled: boolean;
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	hubConnection: HubConnection | null;
}

const InstanceActionMenu: FunctionComponent<Props> = (props) => {
	const { isDisabled, deviceStatuses, deviceStatus, hubConnection } = props;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const { showDialog, closeDialog } = useDialog();
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const userInfo = useUserInfo();
	const isActionAccessible = (accessRight: string) =>
		userInfo.accessRightCodes.some((x) => x === accessRight);

	const handleShowInfoWarningDialog = async () => {
		handleClose();
		await showDialog({
			title: deviceStatus
				? `[${deviceStatus.deviceName}] Infolišta`
				: "Infolišta",
			content: (
				<InfoWarningsDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleShowFeatureDialog = async () => {
		handleClose();
		await showDialog({
			title: deviceStatus
				? `[${deviceStatus.deviceName}] Features`
				: "Features",
			content: (
				<FeaturesDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleShowProductDialog = async () => {
		await showDialog({
			title: deviceStatus
				? `[${deviceStatus.deviceName}] Produkty`
				: "Produkty",
			content: (
				<ProductsDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleShowSystemSettingDialog = async () => {
		handleClose();
		await showDialog({
			title: deviceStatus
				? `[${deviceStatus.deviceName}] System settings`
				: "System settings",
			content: (
				<SystemSettingsDialogContent
					deviceStatuses={deviceStatuses}
					deviceStatus={deviceStatus}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleShowFrequentActionsDialog = async () => {
		handleClose();
		await showDialog({
			title: "Rychlé akce",
			content: (
				<FrequentActionsDialogContent
					deviceStatuses={deviceStatuses}
					hubConnection={hubConnection}
					closeDialog={closeDialog}
				/>
			),
		});
	};

	const handleDeviceStatusSync = (removeDevice: boolean) => {
		handleClose();
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}
		const deviceStatusMessage: DeviceStatusRequestMessage = {
			filteredDeviceNames: !!deviceStatus ? [deviceStatus.deviceName] : [],
			removeDevice: !!deviceStatus ? removeDevice : false,
		};
		hubConnection
			.invoke("deviceStatusSync", deviceStatusMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	return (
		<Stack direction="row" alignItems="center">
			<IconButton
				id={`instance-action-button-${deviceStatus}`}
				aria-controls={
					open ? `instance-action-menu-${deviceStatus}` : undefined
				}
				aria-haspopup="true"
				aria-expanded={open ? "true" : undefined}
				onClick={handleClick}
				sx={{ marginLeft: 2 }}
			>
				<MoreVertIcon />
			</IconButton>
			<StyledMenu
				id={`instance-action-menu-${deviceStatus}`}
				MenuListProps={{
					"aria-labelledby": `instance-action-button-${deviceStatus}`,
				}}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				disableScrollLock={true}
			>
				{((!isDisabled && deviceStatus && !deviceStatus?.isSelfInsurance) ||
					(!deviceStatus && deviceStatuses)) && (
					<div>
						<MenuItem
							onClick={handleShowInfoWarningDialog}
							disableRipple
							disabled={
								deviceStatuses?.length === 0 ||
								!isActionAccessible("InstanceManagementInfoWarnings")
							}
						>
							<AnnouncementIcon />
							Infolišta
						</MenuItem>
						<MenuItem
							onClick={handleShowFeatureDialog}
							disableRipple
							disabled={
								deviceStatuses?.length === 0 ||
								!isActionAccessible("InstanceManagementFeatures")
							}
						>
							<RuleIcon />
							Features
						</MenuItem>
						<MenuItem
							onClick={handleShowProductDialog}
							disableRipple
							disabled={
								deviceStatuses?.length === 0 ||
								!isActionAccessible("InstanceManagementProducts")
							}
						>
							<CategoryIcon />
							Produkty
						</MenuItem>
						<MenuItem
							onClick={handleShowSystemSettingDialog}
							disableRipple
							disabled={
								deviceStatuses?.length === 0 ||
								!isActionAccessible("InstanceManagementSystemSettings")
							}
						>
							<SettingsIcon />
							System settings
						</MenuItem>
					</div>
				)}
				{!isDisabled && !deviceStatus && deviceStatuses && (
					<>
						<Divider sx={{ my: 0.5 }} />
						<MenuItem
							onClick={handleShowFrequentActionsDialog}
							disableRipple
							disabled={deviceStatuses?.length === 0}
						>
							<BoltIcon />
							Rychlé akce
						</MenuItem>

						<BlTooltip
							title="Probudí neaktivní instance a odešle všem instancím požadavek k načtení nejnovějších dat. Proces může trvat i několik minut."
							placement="left"
							enterDelay={1000}
						>
							<MenuItem
								onClick={() => {
									handleDeviceStatusSync(false);
								}}
								disableRipple
								disabled={!isActionAccessible("InstanceManagementSyncDevice")}
							>
								<CloudSyncIcon />
								Synchronizace
							</MenuItem>
						</BlTooltip>
					</>
				)}
				{!!deviceStatus && (
					<>
						{!isDisabled && <Divider sx={{ my: 0.5 }} />}
						<BlTooltip
							title="Manuální odstranění instance."
							placement="left"
							enterDelay={1000}
						>
							<MenuItem
								onClick={() => {
									handleDeviceStatusSync(true);
								}}
								disableRipple
								disabled={
									!isActionAccessible("InstanceManagementSystemSettings")
								}
							>
								<DeleteIcon />
								Odstranit
							</MenuItem>
						</BlTooltip>
					</>
				)}
			</StyledMenu>
		</Stack>
	);
};

export default InstanceActionMenu;
